import { useState, useEffect } from 'react';
import {
  Control,
  Controller,
  FieldValues,
  FieldPath,
  useWatch,
} from 'react-hook-form';
import { QueryKey } from '@tanstack/react-query';
import { cn } from '@/lib/utils.ts';
import InputLabel from '@/components/form/base/InputLabel.tsx';
import InputHint from '@/components/form/base/InputHint.tsx';
import InputError from '@/components/form/base/InputError.tsx';
import PrimitiveAsyncMultiSelect from '@/components/asyncSelect/PrimitiveAsyncMultiSelect.tsx';
import useAsyncSelectOptions from '@/components/asyncSelect/useAsyncSelectOptions.ts';
import Option from '@/components/form/types/Option.ts';
import { PageRequest } from '@/components/table/types/PageRequest.ts';
import { FilterCriteriaDto } from '@/components/table/types/FilterCriteriaDto.tsx';
import { PagedDto } from '@/components/table/types/PagedDto.ts';

export type AsyncMultiSearchSelectProps<T extends FieldValues, TData> = {
  fieldPathName: FieldPath<T>;
  control: Control<T>;
  queryKey: QueryKey;
  fetchFn: (
    pageRequest: PageRequest,
    filter: FilterCriteriaDto,
  ) => Promise<PagedDto<TData>>;
  toOption: (item: TData) => Option;
  label?: string;
  required?: boolean;
  hint?: string;
  error?: string;
  className?: string;
  pageSize?: number;
  placeholder?: string;
  isDisabled?: boolean;
};

const AsyncMultiSearchSelectRHF = <T extends FieldValues, TData>({
  fieldPathName,
  control,
  queryKey,
  fetchFn,
  toOption,
  label,
  required = false,
  hint,
  error,
  className,
  pageSize = 20,
  placeholder,
  isDisabled,
}: AsyncMultiSearchSelectProps<T, TData>) => {
  const [initialOptions, setInitialOptions] = useState<Option[]>([]);
  const { loadOptions, fetchOptionById } = useAsyncSelectOptions({
    queryKey,
    fetchFn,
    toOption,
    pageSize,
  });

  const currentValues = useWatch({ control, name: fieldPathName });

  useEffect(() => {
    const values = (currentValues as string[]) ?? [];
    if (values.length > 0 && initialOptions.length === 0) {
      Promise.all(values.map((id) => fetchOptionById(id))).then((options) => {
        setInitialOptions(options.filter(Boolean) as Option[]);
      });
    }
  }, [currentValues, fetchOptionById, initialOptions.length]);

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <InputLabel label={label} required={required} disabled={isDisabled} />
      )}
      <Controller
        control={control}
        name={fieldPathName}
        render={({ field }) => (
          <PrimitiveAsyncMultiSelect
            name={fieldPathName}
            id={fieldPathName}
            loadOptions={loadOptions}
            value={(field.value as string[]) ?? []}
            initialOptions={initialOptions}
            onChange={(values) => field.onChange(values)}
            error={error}
            pageSize={pageSize}
            placeholder={placeholder}
            isDisabled={isDisabled}
            mobileMenuTitle={label}
          />
        )}
      />
      {error && <InputError error={error} />}
      {hint && <InputHint hint={hint} />}
    </div>
  );
};

export default AsyncMultiSearchSelectRHF;
